// ==================== BILLS SERVICE ====================
// Extracted from database.js - contains all bills (accounts payable) functionality

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../utils/database/firebase-config';
// NOTE: Service dependencies commented out to avoid circular imports during refactoring
// import { vendorsService } from './vendors';
// import { expensesService } from '../accounting/expenses';

export const billsService = {
  async generateBillNumber(companyId) {
    // Generate bill number with date integration
    const today = new Date();
    const year = today.getFullYear().toString().slice(-2);
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    // Get bills count for today
    const billsRef = collection(db, 'bills');
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    
    try {
      const q = query(
        billsRef, 
        where('companyId', '==', companyId),
        where('createdAt', '>=', startOfDay),
        where('createdAt', '<', endOfDay)
      );
      
      const snapshot = await getDocs(q);
      const count = snapshot.size + 1;
      
      return `BILL-${year}${month}${day}-${String(count).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating bill number with query, using fallback:', error);
      // Fallback to timestamp-based generation
      const timestamp = Date.now().toString().slice(-6);
      return `BILL-${year}${month}${day}-${timestamp}`;
    }
  },
  
  async getBills(companyId) {
    try {
      const billsRef = collection(db, 'bills');
      const q = query(billsRef, where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      // Get all vendors for this company to populate vendor information
      const vendorsRef = collection(db, 'vendors');
      const vendorsQuery = query(vendorsRef, where('companyId', '==', companyId));
      const vendorsSnapshot = await getDocs(vendorsQuery);
      const vendors = vendorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const vendorsMap = new Map();
      vendors.forEach(vendor => {
        vendorsMap.set(vendor.id, vendor);
      });
      
      const bills = await Promise.all(snapshot.docs.map(async (doc) => {
        const data = doc.data();
        const vendor = vendorsMap.get(data.vendorId);
        
        // Get actual payments for this bill to calculate accurate paid amount
        const paymentsRef = collection(db, 'vendorPayments');
        const paymentsQuery = query(paymentsRef, 
          where('billId', '==', doc.id),
          where('companyId', '==', companyId)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const payments = paymentsSnapshot.docs.map(payDoc => payDoc.data());
        const actualPaidAmount = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
        
        // Update payment status based on actual payments
        const totalAmount = data.totalAmount || 0;
        let paymentStatus;
        if (actualPaidAmount >= totalAmount && totalAmount > 0) {
          paymentStatus = 'paid';
        } else if (actualPaidAmount > 0) {
          paymentStatus = 'partial';
        } else {
          paymentStatus = 'unpaid';
        }
        
        return {
          ...data,
          id: doc.id,
          vendor: vendor || null,
          vendorName: vendor?.name || vendor?.companyName || data.vendorName || 'Unknown Vendor',
          totalAmount: totalAmount,
          paidAmount: actualPaidAmount,
          remainingAmount: Math.max(0, totalAmount - actualPaidAmount),
          paymentStatus: paymentStatus,
          status: paymentStatus === 'paid' ? 'paid' : (data.status || 'pending')
        };
      }));
      
      return bills.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Error with ordered query for bills, falling back to simple query:', error);
      // Fallback to simple query without orderBy if index doesn't exist
      const billsRef = collection(db, 'bills');
      const q = query(billsRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);
      
      // Get all vendors for this company to populate vendor information
      const vendorsRef = collection(db, 'vendors');
      const vendorsQuery = query(vendorsRef, where('companyId', '==', companyId));
      const vendorsSnapshot = await getDocs(vendorsQuery);
      const vendors = vendorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const vendorsMap = new Map();
      vendors.forEach(vendor => {
        vendorsMap.set(vendor.id, vendor);
      });
      
      const bills = await Promise.all(snapshot.docs.map(async (doc) => {
        const data = doc.data();
        const vendor = vendorsMap.get(data.vendorId);
        
        // Get actual payments for this bill to calculate accurate paid amount
        const paymentsRef = collection(db, 'vendorPayments');
        const paymentsQuery = query(paymentsRef, 
          where('billId', '==', doc.id),
          where('companyId', '==', companyId)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const payments = paymentsSnapshot.docs.map(payDoc => payDoc.data());
        const actualPaidAmount = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
        
        // Update payment status based on actual payments
        const totalAmount = data.totalAmount || 0;
        let paymentStatus;
        if (actualPaidAmount >= totalAmount && totalAmount > 0) {
          paymentStatus = 'paid';
        } else if (actualPaidAmount > 0) {
          paymentStatus = 'partial';
        } else {
          paymentStatus = 'unpaid';
        }
        
        return {
          ...data,
          id: doc.id,
          vendor: vendor || null,
          vendorName: vendor?.name || vendor?.companyName || data.vendorName || 'Unknown Vendor',
          totalAmount: totalAmount,
          paidAmount: actualPaidAmount,
          remainingAmount: Math.max(0, totalAmount - actualPaidAmount),
          paymentStatus: paymentStatus,
          status: paymentStatus === 'paid' ? 'paid' : (data.status || 'pending')
        };
      }));
      
      // Sort in JavaScript as fallback
      return bills.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dateB - dateA;
      });
    }
  },
  
  async getBill(billId) {
    const billRef = doc(db, 'bills', billId);
    const snapshot = await getDoc(billRef);
    if (snapshot.exists()) {
      const data = snapshot.data();
      // Get vendor information
      // TODO: Reconnect with vendorsService after refactoring
      // const vendor = await vendorsService.getVendor(data.vendorId);
      const vendor = null; // Temporary placeholder
      return { 
        ...data, 
        id: snapshot.id,
        vendor: vendor || null,
        vendorName: vendor?.name || vendor?.companyName || data.vendorName || 'Unknown Vendor'
      };
    }
    return null;
  },
  
  async addBill(billData) {
    const billsRef = collection(db, 'bills');
    const docRef = await addDoc(billsRef, {
      ...billData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'pending',
      paymentStatus: 'unpaid',
      paidAmount: 0
    });
    
    // Create corresponding expense entry for accounts payable tracking
    if (billData.totalAmount > 0) {
      try {
        const expenseData = {
          companyId: billData.companyId,
          date: billData.billDate || new Date().toISOString().split('T')[0],
          category: billData.category || 'other',
          vendor: billData.vendorName || 'Unknown Vendor',
          description: billData.description || `Bill ${billData.billNumber}`,
          amount: billData.totalAmount,
          paymentMethod: 'accounts_payable',
          reference: `Bill: ${billData.billNumber}`,
          vatRate: billData.taxRate || 0,
          vatAmount: billData.taxAmount || 0,
          totalAmount: billData.totalAmount,
          notes: billData.notes || `Auto-generated from bill ${billData.billNumber}`,
          status: 'pending_payment',
          createdBy: billData.createdBy,
          billId: docRef.id,
          autoGenerated: true,
          paymentStatus: 'unpaid'
        };
        
        // Create expense without journal entry (journal entry will be created when payment is made)
        // TODO: Reconnect with expensesService after refactoring
        // await expensesService.addExpense(expenseData, true);
        
        // Temporary placeholder - create expense directly
        const expenseRef = collection(db, 'expenses');
        await addDoc(expenseRef, {
          ...expenseData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error creating expense for bill:', error);
        // Don't fail the bill creation if expense creation fails
      }
    }
    
    return docRef.id;
  },
  
  async updateBill(billId, billData) {
    const billRef = doc(db, 'bills', billId);
    await updateDoc(billRef, { ...billData, updatedAt: serverTimestamp() });
    return true;
  },
  
  async deleteBill(billId) {
    const billRef = doc(db, 'bills', billId);
    await deleteDoc(billRef);
    return true;
  },
  
  async updateBillPaymentStatus(billId, paidAmount, status = 'partial') {
    const billRef = doc(db, 'bills', billId);
    const billSnap = await getDoc(billRef);
    
    if (billSnap.exists()) {
      const billData = billSnap.data();
      const totalAmount = billData.totalAmount || 0;
      const newPaidAmount = paidAmount;
      
      let paymentStatus;
      if (newPaidAmount >= totalAmount) {
        paymentStatus = 'paid';
      } else if (newPaidAmount > 0) {
        paymentStatus = 'partial';
      } else {
        paymentStatus = 'unpaid';
      }
      
      await updateDoc(billRef, {
        paidAmount: newPaidAmount,
        paymentStatus: paymentStatus,
        updatedAt: serverTimestamp()
      });
      
      return true;
    }
    return false;
  }
}; 